import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Card, Col, Progress, Row, Space, Table, Tabs, Tag, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  AlertOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DatabaseOutlined,
  EyeOutlined,
  HeartOutlined,
  PlayCircleOutlined,
  RobotOutlined,
  UserOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import { get } from '../../api/request';
import { API_ENDPOINTS, buildApiUrl } from '../../api/config';
import './Dashboard.css';

interface DashboardSummaryCard {
  key: string;
  title: string;
  value: number;
  suffix?: string;
  prefix?: string;
  trend?: string;
  trend_type?: 'up' | 'down';
  desc: string;
  precision?: number;
}

interface DashboardNameValueItem {
  label: string;
  value: number;
  percent?: number;
  color?: string;
}

interface DashboardVideoTopItem {
  id: string;
  title: string;
  channel: string;
  source_platform: string;
  cover_url: string;
  duration_seconds: number;
  view_count: number;
  favorite_count: number;
  playable: boolean;
}

interface DashboardAICostSummary {
  today_calls: number;
  avg_cost: string;
  max_single_cost: string;
  failure_rate: string;
}

interface DashboardTodoItem {
  title: string;
  count: number;
  desc: string;
  tone: string;
}

interface DashboardQuickMetric {
  label: string;
  value: string;
  hint: string;
}

interface DashboardOverview {
  generated_at: string;
  summary_cards: DashboardSummaryCard[];
  channel_stats: DashboardNameValueItem[];
  video_top_by_views: DashboardVideoTopItem[];
  video_top_by_favorites: DashboardVideoTopItem[];
  ai_cost_summary: DashboardAICostSummary;
  todo_list: DashboardTodoItem[];
  quick_metrics: DashboardQuickMetric[];
}

const DEFAULT_OVERVIEW: DashboardOverview = {
  generated_at: new Date().toISOString(),
  summary_cards: [],
  channel_stats: [],
  video_top_by_views: [],
  video_top_by_favorites: [],
  ai_cost_summary: {
    today_calls: 0,
    avg_cost: '¥0.0000',
    max_single_cost: '¥0.0000',
    failure_rate: '0.0%',
  },
  todo_list: [],
  quick_metrics: [],
};

const summaryMeta: Record<string, { icon: React.ReactNode; color: string }> = {
  users: { icon: <UserOutlined />, color: '#1677ff' },
  videos: { icon: <VideoCameraOutlined />, color: '#13c2c2' },
  views: { icon: <EyeOutlined />, color: '#722ed1' },
  ai_calls: { icon: <RobotOutlined />, color: '#fa8c16' },
};

const channelMap: Record<string, string> = {
  wake: '起床铃声',
  sleep: '晚安哄睡',
  male: '男声陪伴',
  female: '女声陪伴',
};

const todoIconMap: Record<string, React.ReactNode> = {
  danger: <AlertOutlined />,
  warning: <ClockCircleOutlined />,
  info: <DatabaseOutlined />,
  success: <CheckCircleOutlined />,
};

const formatRefreshTime = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
};

const formatDuration = (seconds: number) => {
  const sec = Number(seconds) || 0;
  if (sec <= 0) return '-';
  return `${Math.round(sec / 60)} 分钟`;
};

const formatCount = (value: number) => Number(value || 0).toLocaleString('zh-CN');

const videoColumns: ColumnsType<DashboardVideoTopItem & { rank: number; key: string }> = [
  {
    title: '排名',
    dataIndex: 'rank',
    width: 64,
    render: (rank: number) => <span className={`rank-badge rank-${rank}`}>{rank}</span>,
  },
  {
    title: '视频',
    dataIndex: 'title',
    render: (_, record) => (
      <div className="video-title-cell">
        <div className="video-cover">
          {record.cover_url ? <img src={record.cover_url} alt="" /> : <PlayCircleOutlined />}
        </div>
        <div>
          <strong>{record.title}</strong>
          <span>{channelMap[record.channel] || record.channel || '-'} · {formatDuration(record.duration_seconds)}</span>
        </div>
      </div>
    ),
  },
  {
    title: '播放',
    dataIndex: 'view_count',
    width: 96,
    render: (value: number) => <span className="metric-cell"><EyeOutlined />{formatCount(value)}</span>,
  },
  {
    title: '收藏',
    dataIndex: 'favorite_count',
    width: 96,
    render: (value: number) => <span className="metric-cell"><HeartOutlined />{formatCount(value)}</span>,
  },
  {
    title: '来源',
    dataIndex: 'source_platform',
    width: 108,
    render: (value: string) => <Tag color={value === 'B站助眠' ? 'blue' : 'orange'}>{value || '-'}</Tag>,
  },
];

const Dashboard: React.FC = () => {
  const [overview, setOverview] = useState<DashboardOverview>(DEFAULT_OVERVIEW);

  const loadOverview = useCallback(async () => {
    try {
      const data = await get<DashboardOverview>(buildApiUrl(API_ENDPOINTS.dashboard.overview));
      setOverview({ ...DEFAULT_OVERVIEW, ...data });
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载数据看板失败');
    }
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const topByViews = overview.video_top_by_views.map((item, index) => ({ ...item, key: item.id, rank: index + 1 }));
  const topByFavorites = overview.video_top_by_favorites.map((item, index) => ({ ...item, key: item.id, rank: index + 1 }));

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <div>
          <h1 className="page-title">数据看板</h1>
          <p className="dashboard-subtitle">聚焦用户端资源运营、视频热度、内容健康和 AI 成本。</p>
        </div>
        <div className="dashboard-header-status">
          <Badge status="processing" text="运营数据实时汇总" />
          <span>最近刷新 {formatRefreshTime(overview.generated_at)}</span>
        </div>
      </div>

      <Row gutter={[16, 16]} className="dashboard-summary">
        {overview.summary_cards.map((item) => {
          const meta = summaryMeta[item.key] || summaryMeta.videos;
          return (
            <Col xs={24} sm={12} xl={6} key={item.key}>
              <Card className="dashboard-stat-card" bordered={false}>
                <div className="dashboard-stat-top">
                  <div className="dashboard-stat-icon" style={{ color: meta.color }}>{meta.icon}</div>
                  {item.trend ? <Tag color={item.trend_type === 'down' ? 'orange' : 'green'}>{item.trend}</Tag> : <Tag>累计</Tag>}
                </div>
                <div className="dashboard-stat-title">{item.title}</div>
                <div className="dashboard-stat-value">
                  {item.prefix && <span className="dashboard-stat-prefix">{item.prefix}</span>}
                  {Number(item.value || 0).toLocaleString('zh-CN', {
                    minimumFractionDigits: item.precision || 0,
                    maximumFractionDigits: item.precision || 0,
                  })}
                  {item.suffix && <span className="dashboard-stat-suffix">{item.suffix}</span>}
                </div>
                <div className="dashboard-stat-desc">{item.desc}</div>
              </Card>
            </Col>
          );
        })}
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={16}>
          <Card className="dashboard-panel" title={<Space><VideoCameraOutlined />视频 Top 排名</Space>} bordered={false}>
            <Tabs
              items={[
                {
                  key: 'views',
                  label: '按播放量',
                  children: (
                    <Table
                      columns={videoColumns}
                      dataSource={topByViews}
                      locale={{ emptyText: '暂无视频播放数据' }}
                      pagination={false}
                      scroll={{ x: 760 }}
                      size="small"
                      rowClassName="dashboard-table-row"
                    />
                  ),
                },
                {
                  key: 'favorites',
                  label: '按收藏量',
                  children: (
                    <Table
                      columns={videoColumns}
                      dataSource={topByFavorites}
                      locale={{ emptyText: '暂无视频收藏数据' }}
                      pagination={false}
                      scroll={{ x: 760 }}
                      size="small"
                      rowClassName="dashboard-table-row"
                    />
                  ),
                },
              ]}
            />
          </Card>
        </Col>

        <Col xs={24} xl={8}>
          <Card className="dashboard-panel" title={<Space><DatabaseOutlined />频道资源覆盖</Space>} bordered={false}>
            <div className="channel-list">
              {overview.channel_stats.map((item) => (
                <div className="channel-item" key={item.label}>
                  <div className="channel-row">
                    <span>{item.label}</span>
                    <strong>{item.value} 条</strong>
                  </div>
                  <Progress percent={item.percent || 0} showInfo={false} strokeColor={item.color} trailColor="#edf1f6" />
                </div>
              ))}
            </div>
          </Card>

          <Card className="dashboard-panel dashboard-panel-spaced" title={<Space><RobotOutlined />AI 调用成本</Space>} bordered={false}>
            <div className="ai-cost-grid">
              <div><span>今日调用</span><strong>{overview.ai_cost_summary.today_calls}</strong></div>
              <div><span>平均费用</span><strong>{overview.ai_cost_summary.avg_cost}</strong></div>
              <div><span>最高单次</span><strong>{overview.ai_cost_summary.max_single_cost}</strong></div>
              <div><span>失败率</span><strong>{overview.ai_cost_summary.failure_rate}</strong></div>
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} className="dashboard-section">
        <Col xs={24} xl={14}>
          <Card className="dashboard-panel" title={<Space><AlertOutlined />待处理事项</Space>} bordered={false}>
            <div className="todo-list">
              {overview.todo_list.map((item) => (
                <div className={`todo-item ${item.tone}`} key={item.title}>
                  <div className="todo-icon">{todoIconMap[item.tone] || <CheckCircleOutlined />}</div>
                  <div className="todo-content">
                    <div className="todo-title">
                      <span>{item.title}</span>
                      <strong>{item.count}</strong>
                    </div>
                    <p>{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </Col>

        <Col xs={24} xl={10}>
          <Row gutter={[16, 16]}>
            {overview.quick_metrics.map((item) => (
              <Col xs={24} sm={12} key={item.label}>
                <div className="quick-metric">
                  <span>{item.label}</span>
                  <strong>{item.value}</strong>
                  <p>{item.hint}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
